import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

import { BlockTextComponent } from '../../common/block-text/block-text.component';
import { FieldEditorV2Component } from '../../base-template-editor-v2/field-editor-v2/field-editor-v2.component';
import { InlineEditV2Directive } from '../../base-template-editor-v2/inline-edit/inline-edit-v2.directive';
import { InlineMediaV2Directive } from '../../base-template-editor-v2/inline-edit/inline-media-v2.directive';
import { BaseComponent } from '../../common/BaseComponent';
import { Helper } from '../../../../utils/helper';
import {
  ZigZagNoHeadingNoButtonInitModel,
  ZigZagNoHeadingNoButtonInitVariables,
  ZigZagNoHeadingNoButtonModel,
  ZigZagNoHeadingNoButtonVariables,
} from '../../../../models/email-template/zigzag-no-heading-no-button.interface';

/**
 * Mockup-styled ZigZag No Heading + No Button block - a drop-in replacement
 * for app-zigzag-no-heading-no-button in block-renderer-v2 (same
 * [block]/[isPreview] inputs and outputs), so it covers both the
 * center-canvas summary (isPreview=true) and the right-panel edit
 * form (isPreview=false). Reuses the real, unmodified app-block-text (image
 * upload/segment/validation) and app-field-editor-v2/app-pin sub-components
 * and the exact same ZigZagNoHeadingNoButtonVariables field names - only the
 * surrounding layout is new. Simplest ZigZag variant: image left, plain
 * paragraph right, no heading and no CTA - text aligns to the image's top
 * edge rather than centering (matches the mockup's own no-heading treatment).
 */
@Component({
  selector: 'app-zigzag-no-heading-no-button-v2',
  imports: [CommonModule, ReactiveFormsModule, BlockTextComponent, FieldEditorV2Component, InlineEditV2Directive, InlineMediaV2Directive],
  templateUrl: './zigzag-no-heading-no-button-v2.component.html',
  styleUrl: './zigzag-no-heading-no-button-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ZigZagNoHeadingNoButtonComponentV2 extends BaseComponent implements OnInit {
  @Input() block: ZigZagNoHeadingNoButtonModel = new ZigZagNoHeadingNoButtonInitModel();
  @Input() isPreview = false;
  @Input() disabled = false;

  @Output() blockData = new EventEmitter<any>();
  @Output() removeBlockEvent = new EventEmitter<string | null>();

  ngOnInit() {
    if (Helper.isEmpty(this.block.variables!)) {
      this.block.variables = new ZigZagNoHeadingNoButtonInitVariables();
    }
  }

  imagePlaceholder = 'Image';
  imageHrefPlaceholder = 'Enter Image Redirect Link';
  imageAltPlaceholder = 'Enter Image Alt Text';
  textPlaceholder = 'Enter Text';

  protected imageAsUpload = true;

  protected toggleImageMode(): void {
    this.imageAsUpload = !this.imageAsUpload;
  }

  get imagePreviewHref(): string {
    const base = this.helper.getFullUrl(this.block.variables?.['ZigZagNhnbImageHref']);
    const segment = this.block.variables?.['ZigZagNhnbImageHrefSegmentRegex'];
    if (!segment) {
      return base;
    }
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}segment=${encodeURIComponent(segment)}`;
  }

  removeBlock(): void {
    this.removeBlockEvent.emit(this.block.blockId);
  }

  onChange(value: any, fieldName: keyof ZigZagNoHeadingNoButtonVariables) {
    if (this.block.variables) {
      (this.block.variables as any)[fieldName] = value;
      this.blockData.emit(this.block);
    }
  }


  handleOnSegmentChange(segment: string, blockName: string) {
    if (this.block.variables) {
      (this.block.variables as any)[blockName] = segment;
      this.blockData.emit(this.block);
    }
  }
}
